import * as React from "react";
import { Box, Typography, Stack } from "@mui/material";
import type { LinkLock, PassWindow } from "./passWindow";
import { DataField, MCS_COLORS, Panel, StatusLamp, formatClock } from "./mcsUi";
import { formatDataSize } from "./payloadResults";

export type PayloadState = "off" | "scheduled" | "recording" | "stored" | "downlinking" | "received";

export const PAYLOAD_TM: Record<PayloadState, { label: string; color: string }> = {
  off: { label: "OFF", color: MCS_COLORS.idle },
  scheduled: { label: "ON · WAITING FOR TARGET", color: MCS_COLORS.info },
  recording: { label: "ON · ACQUIRING", color: MCS_COLORS.ok },
  stored: { label: "DATA STORED", color: MCS_COLORS.warn },
  downlinking: { label: "DOWNLINKING", color: MCS_COLORS.info },
  received: { label: "IDLE · DATA DELIVERED", color: MCS_COLORS.ok },
};

// Housekeeping telemetry of the satellite, as received during a pass:
// simulated, but behaving plausibly (battery charging in sunlight,
// temperatures following the orbit). Outside a pass there is no
// telemetry -- the last values are shown greyed out.
export default function TelemetryPanel(props: {
  pw: PassWindow;
  link: LinkLock;
  orbitMinutes: number;
  payloadName: string;
  payloadState: PayloadState;
  storedKb: number;
}) {
  const { pw, link, orbitMinutes, payloadName, payloadState, storedKb } = props;
  const live = link.frameSync;
  const lastFrame = React.useRef<Date | null>(null);
  if (live) lastFrame.current = pw.simNow;

  const t = (lastFrame.current ?? pw.simNow).getTime() / 1000;
  const phase = (2 * Math.PI * t) / (orbitMinutes * 60);
  const sunlit = Math.sin(phase) > -0.35;
  const soc = 82 + 9 * Math.sin(phase - 0.6);
  const voltage = 7.4 + 0.009 * soc;
  const solarA = sunlit ? 0.9 + 0.25 * Math.sin(phase) : 0;
  const obcTemp = 17 + 5 * Math.sin(phase - 1);
  const payloadTemp = 12 + 6 * Math.sin(phase - 1.3) + (payloadState === "recording" || payloadState === "downlinking" ? 4 : 0);
  const framesSinceAos = pw.inContact && pw.pass ? Math.max(0, Math.floor((pw.simNow.getTime() - pw.pass.aos.getTime()) / 1000 - 5) * 4) : 0;
  const signalDb = pw.look ? 4 + Math.max(0, pw.look.elevationDeg) * 0.16 : 0;
  const payload = PAYLOAD_TM[payloadState];

  return (
    <Panel
      title="Spacecraft Telemetry"
      right={
        <StatusLamp
          color={live ? MCS_COLORS.ok : MCS_COLORS.idle}
          label={live ? "TM LIVE" : lastFrame.current ? `LAST TM ${formatClock(lastFrame.current)}` : "NO TM"}
          pulsing={live}
        />
      }
    >
      {!live && !lastFrame.current ? (
        <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 12.5, lineHeight: 1.55 }}>
          No telemetry yet. It arrives once the station has locked onto your satellite's signal during a pass.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5 }}>
            <DataField label="Mode" value="NOMINAL" color={MCS_COLORS.ok} stale={!live} />
            <DataField label="Battery" value={`${voltage.toFixed(2)} V · ${Math.round(soc)}%`} stale={!live} />
            <DataField label="Solar array" value={sunlit ? `${solarA.toFixed(2)} A` : "0.00 A · eclipse"} stale={!live} />
            <DataField label="OBC temp" value={`${obcTemp.toFixed(1)} °C`} stale={!live} />
            <DataField label="Payload temp" value={`${payloadTemp.toFixed(1)} °C`} stale={!live} />
            <DataField label="Signal Eb/N0" value={live ? `${signalDb.toFixed(1)} dB` : "—"} stale={!live} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5 }}>
            <Box sx={{ gridColumn: "span 2" }}>
              <DataField label={`Payload · ${payloadName}`} value={payload.label} color={payload.color} stale={!live} />
            </Box>
            <DataField label="Stored data" value={formatDataSize(storedKb)} stale={!live} />
          </Box>
          <Typography sx={{ fontFamily: "inherit", fontSize: 11, color: "rgba(226,232,240,0.45)" }}>
            {live ? `${framesSinceAos.toLocaleString("en-US")} telemetry frames received this pass` : "Out of contact: values from the last pass"}
          </Typography>
        </Stack>
      )}
    </Panel>
  );
}
